import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import StudioAuditBoard from '../components/aiAudit/StudioAuditBoard';
import axios from 'axios';
import AdminLayout from '../components/AdminLayout';
import Table from '../components/Table';
import Pagination from '../components/Pagination';
import toast from 'react-hot-toast';
import {
  ArrowPathIcon,
  ChartBarIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  EyeIcon,
  MagnifyingGlassIcon,
  SparklesIcon,
} from '@heroicons/react/24/outline';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001';

const statusClasses = {
  success: 'bg-green-100 text-green-800',
  partial: 'bg-yellow-100 text-yellow-800',
  failed: 'bg-red-100 text-red-800',
  error: 'bg-red-100 text-red-800',
  no_change: 'bg-gray-100 text-gray-700',
  ask: 'bg-sky-100 text-sky-800',
  pending_user_choice: 'bg-blue-100 text-blue-800',
};

const statusLabels = {
  success: 'Success',
  partial: 'Partial',
  failed: 'Failed',
  error: 'Error',
  no_change: 'No change',
  ask: 'Ask',
  pending_user_choice: 'User choice',
};

const modeClasses = {
  ask: 'bg-sky-50 text-sky-700 ring-sky-200',
  change: 'bg-gray-50 text-gray-600 ring-gray-200',
  legal: 'bg-violet-50 text-violet-700 ring-violet-200',
};

const modeLabels = {
  ask: 'Ask mode',
  change: 'Change mode',
  legal: 'Legal mode',
};

// Which AI surface produced the turn. Rows written before the studio was
// audited have no surface and are website-editor turns by definition.
const surfaceClasses = {
  website_editor: 'bg-indigo-50 text-indigo-700 ring-indigo-200',
  crm_ui_studio: 'bg-fuchsia-50 text-fuchsia-700 ring-fuchsia-200',
};

const surfaceLabels = {
  website_editor: 'Website editor',
  crm_ui_studio: 'BOS Design Studio',
};

// Opening AI Audit with no surface lands on the BOS studio list.
// `surface=all` is the explicit mixed list. An unknown value falls through
// to the studio so a stale bookmark cannot reopen the editor table.
function resolveAuditSurface(surfaceParam) {
  if (surfaceParam === 'all') return '';
  if (surfaceParam === 'website_editor' || surfaceParam === 'crm_ui_studio') return surfaceParam;
  return 'crm_ui_studio';
}

const outcomeClasses = {
  hard_error: 'bg-red-100 text-red-800',
  request_failure: 'bg-rose-100 text-rose-800',
  action_failure: 'bg-orange-100 text-orange-800',
  verification_failure: 'bg-purple-100 text-purple-800',
  guardrail_rejection: 'bg-amber-100 text-amber-800',
  recovered_partial: 'bg-yellow-100 text-yellow-800',
  recovered_success: 'bg-emerald-100 text-emerald-800',
};

const outcomeLabels = {
  hard_error: 'Hard error',
  request_failure: 'Request failure',
  action_failure: 'Action failure',
  verification_failure: 'Verification failure',
  guardrail_rejection: 'Guardrail',
  recovered_partial: 'Recovered partial',
  recovered_success: 'Recovered success',
};

function formatDuration(ms) {
  if (!ms && ms !== 0) return '—';
  if (ms < 1000) return `${ms}ms`;
  return `${(ms / 1000).toFixed(1)}s`;
}

function getAuditMode(row) {
  return row?.advisor_mode || 'change';
}

function getAuditSurface(row) {
  return row?.surface || 'website_editor';
}

function isStudioRow(row) {
  return getAuditSurface(row) === 'crm_ui_studio';
}

function getDisplayStatus(row) {
  const mode = getAuditMode(row);
  if (mode === 'ask' && row?.status === 'no_change') return 'ask';
  return row?.status || 'no_change';
}

function StatusBadge({ row }) {
  const status = getDisplayStatus(row);
  return (
    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${statusClasses[status] || statusClasses.no_change}`}>
      {statusLabels[status] || status}
    </span>
  );
}

function ModeBadge({ mode }) {
  const key = mode || 'change';
  return (
    <span className={`inline-flex px-1.5 py-0.5 text-[10px] font-medium rounded-full ring-1 ${modeClasses[key] || modeClasses.change}`}>
      {modeLabels[key] || `${key} mode`}
    </span>
  );
}

function SurfaceBadge({ surface }) {
  const key = surface || 'website_editor';
  return (
    <span className={`inline-flex px-1.5 py-0.5 text-[10px] font-medium rounded-full ring-1 ${surfaceClasses[key] || surfaceClasses.website_editor}`}>
      {surfaceLabels[key] || key}
    </span>
  );
}

function OutcomeBadge({ outcome }) {
  if (!outcome) return <span className="text-xs text-gray-400">—</span>;
  return (
    <span className={`inline-flex px-2 py-0.5 text-[11px] font-semibold rounded-full ${outcomeClasses[outcome] || 'bg-gray-100 text-gray-700'}`}>
      {outcomeLabels[outcome] || outcome}
    </span>
  );
}

function StatCard({ label, value, helper, icon }) {
  return (
    <div className="bg-white rounded-lg shadow p-4">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-gray-500">{label}</p>
          <p className="text-2xl font-semibold text-gray-900">{value ?? '—'}</p>
          {helper && <p className="mt-1 text-xs text-gray-500">{helper}</p>}
        </div>
        {icon}
      </div>
    </div>
  );
}

const AICustomizationAudits = () => {
  const navigate = useNavigate();
  const [audits, setAudits] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState('desc');
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [searchParams, setSearchParams] = useSearchParams();
  const surface = resolveAuditSurface(searchParams.get('surface'));
  const isStudio = surface === 'crm_ui_studio';
  const [outcomeLabel, setOutcomeLabel] = useState('');
  const [hasFailures, setHasFailures] = useState('');
  const [minIterations, setMinIterations] = useState('');
  const [minVisionChecks, setMinVisionChecks] = useState('');
  const [studioResult, setStudioResult] = useState('');
  const [turnMode, setTurnMode] = useState('');

  const setSurface = (value) => {
    const next = new URLSearchParams(searchParams);
    if (value === 'crm_ui_studio' || value === 'website_editor') next.set('surface', value);
    else next.set('surface', 'all');
    setSearchParams(next, { replace: true });
    setStudioResult('');
    setTurnMode('');
    setCurrentPage(1);
  };

  const fetchAudits = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: currentPage,
        limit: 20,
        sortBy,
        sortOrder,
        ...(search && { search }),
        ...(surface && { surface }),
        ...(isStudio ? {
          ...(studioResult && { studioResult }),
          ...(turnMode && { turnMode }),
          ...(hasFailures && { hasFailures }),
          ...(minIterations && { minIterations }),
        } : {
          ...(status && { status }),
          ...(outcomeLabel && { outcomeLabel }),
          ...(hasFailures && { hasFailures }),
          ...(minIterations && { minIterations }),
          ...(minVisionChecks && { minVisionChecks }),
        }),
      });

      const response = await axios.get(`${API_BASE_URL}/api/admin/ai-customizations?${params}`);
      setAudits(response.data.data.audits || []);
      setTotalPages(response.data.data.pagination?.totalPages || 1);
      setTotal(response.data.data.pagination?.total || 0);
    } catch (error) {
      console.error('Error fetching AI audits:', error);
      toast.error('Failed to fetch AI audits');
    } finally {
      setLoading(false);
    }
  }, [currentPage, sortBy, sortOrder, search, status, surface, isStudio, outcomeLabel, hasFailures, minIterations, minVisionChecks, studioResult, turnMode]);

  // The headline metrics follow the surface filter. Editor and studio turns
  // have different shapes (a studio turn runs no intent checks at all), so a
  // blended average would misrepresent both.
  const fetchStats = useCallback(async () => {
    try {
      const params = new URLSearchParams({ ...(surface && { surface }) });
      const response = await axios.get(`${API_BASE_URL}/api/admin/ai-customizations/stats?${params}`);
      setStats(response.data.data);
    } catch (error) {
      console.error('Error fetching AI audit stats:', error);
    }
  }, [surface]);

  useEffect(() => {
    fetchAudits();
  }, [fetchAudits]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  const handleSearch = (event) => {
    event.preventDefault();
    setSearch(searchInput);
    setCurrentPage(1);
  };

  const handleSort = (field, order) => {
    setSortBy(field);
    setSortOrder(order);
    setCurrentPage(1);
  };

  const columns = [
    {
      key: 'status',
      label: 'Status',
      width: '120px',
      render: (row) => <StatusBadge row={row} />,
    },
    {
      key: 'outcome_label',
      label: 'Outcome',
      width: '155px',
      render: (row) => <OutcomeBadge outcome={row.outcome_label} />,
    },
    {
      key: 'prompt',
      label: 'Prompt',
      sortable: false,
      render: (row) => (
        <div className="max-w-sm">
          <p className="text-sm text-gray-900 truncate" title={row.prompt}>
            {row.prompt_preview || row.prompt}
          </p>
          <div className="mt-1 flex flex-wrap items-center gap-1.5">
            <SurfaceBadge surface={getAuditSurface(row)} />
            {/* advisor_mode is an editor concept — a studio turn has no mode. */}
            {!isStudioRow(row) && <ModeBadge mode={getAuditMode(row)} />}
            {!isStudioRow(row) && getAuditMode(row) === 'ask' && (
              <span className="text-[11px] text-gray-500">Informational reply; no site mutation expected</span>
            )}
          </div>
          {row.error_message && (
            <p className="mt-1 text-xs text-red-600 truncate" title={row.error_message}>
              {row.error_message}
            </p>
          )}
        </div>
      ),
    },
    {
      key: 'website',
      label: 'Website / User',
      sortable: false,
      render: (row) => (
        <div className="text-sm">
          {row.website_name ? (
            <Link
              to={`/websites/${row.website_id}`}
              onClick={(event) => event.stopPropagation()}
              className="font-medium text-primary-700 hover:underline"
            >
              {row.website_name}
            </Link>
          ) : row.account_id ? (
            <Link
              to={`/accounts/${row.account_id}`}
              onClick={(event) => event.stopPropagation()}
              className="font-medium text-primary-700 hover:underline"
              title="Site-less account — no website row"
            >
              {row.account_name || 'No site'}
            </Link>
          ) : (
            <span className="text-gray-500">Unknown website</span>
          )}
          <div className="text-xs text-gray-500">{row.user_email || row.user_name || 'Unknown user'}</div>
        </div>
      ),
    },
    {
      key: 'model_rounds_attempted',
      label: 'Rounds',
      width: '95px',
      render: (row) => (
        <span className="font-mono text-sm">{row.model_rounds_attempted}</span>
      ),
    },
    {
      key: 'intent_checks_used',
      label: 'Visions',
      width: '95px',
      // Intent checks are an editor-loop mechanism. Printing 0 for a studio
      // turn would read as "it skipped verification" rather than "this metric
      // does not apply here".
      render: (row) => (isStudioRow(row) ? (
        <span className="text-sm text-gray-400" title="Intent checks are specific to the website editor loop">—</span>
      ) : (
        <span className="font-mono text-sm" title={`${row.visual_verification_count || 0} visual verification event(s), ${row.screenshots_captured || 0} screenshot(s)`}>
          {row.intent_checks_used || 0}
        </span>
      )),
    },
    {
      key: 'actions_failed',
      label: 'Failures',
      width: '95px',
      render: (row) => (
        <span className={`font-mono text-sm ${row.actions_failed > 0 ? 'text-red-600 font-semibold' : 'text-gray-600'}`}>
          {row.actions_failed}
        </span>
      ),
    },
    {
      key: 'duration_ms',
      label: 'Duration',
      width: '100px',
      render: (row) => <span className="text-sm text-gray-600">{formatDuration(row.duration_ms)}</span>,
    },
    {
      key: 'created_at',
      label: 'Created',
      width: '150px',
      render: (row) => (
        <div className="text-xs text-gray-500">
          <div>{new Date(row.created_at).toLocaleDateString()}</div>
          <div>{new Date(row.created_at).toLocaleTimeString()}</div>
        </div>
      ),
    },
  ];

  return (
    <AdminLayout>
      <div>
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">
              AI Audit
            </h1>
            <p className="text-gray-500 mt-1">
              {isStudio
                ? 'Did the turn ship a skin, wait on the merchant, or stop?'
                : 'Inspect AI customization outcomes, retries, visual checks, and tool failures'}
              {total > 0 && <span className="ml-1">({total} total)</span>}
            </p>
          </div>
          <button
            onClick={() => { fetchAudits(); fetchStats(); }}
            disabled={loading}
            className="p-2 text-gray-600 hover:text-primary-600 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
            title="Refresh data"
          >
            <ArrowPathIcon className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>

        {!isStudio && stats && (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-4 mb-4">
            <StatCard
              label="Total Turns"
              value={stats.total}
              helper={stats.surfaceCounts && !surface
                ? `${stats.surfaceCounts.website_editor || 0} editor · ${stats.surfaceCounts.crm_ui_studio || 0} studio`
                : `${stats.last24h || 0} in last 24h`}
              icon={<SparklesIcon className="w-6 h-6 text-primary-500" />}
            />
            <StatCard
              label="Failure / Partial Rate"
              value={`${stats.failureRate || 0}%`}
              helper={`${stats.failed || 0} failed, ${stats.partial || 0} partial`}
              icon={<ExclamationTriangleIcon className="w-6 h-6 text-red-500" />}
            />
            <StatCard
              label="Avg. Rounds"
              value={stats.avgRounds}
              helper="Model rounds per turn"
              icon={<ChartBarIcon className="w-6 h-6 text-blue-500" />}
            />
            <StatCard
              label="Avg. Visions"
              value={stats.avgIntentChecks}
              helper="Intent checks per turn"
              icon={<EyeIcon className="w-6 h-6 text-purple-500" />}
            />
            <StatCard
              label="Avg. Duration"
              value={formatDuration(stats.avgDurationMs)}
              helper="Turn completion time"
              icon={<ClockIcon className="w-6 h-6 text-orange-500" />}
            />
          </div>
        )}

        {!isStudio && stats?.outcomeCounts && (
          <div className="bg-white rounded-lg shadow p-4 mb-6">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-sm font-medium text-gray-700">Outcome breakdown</span>
              {Object.entries(outcomeLabels).map(([key, label]) => (
                <span key={key} className={`inline-flex items-center gap-1 px-2 py-1 text-xs rounded-full ${outcomeClasses[key]}`}>
                  <span>{label}</span>
                  <span className="font-mono font-semibold">{stats.outcomeCounts[key] || 0}</span>
                </span>
              ))}
            </div>
          </div>
        )}

        <div className="bg-white rounded-lg shadow p-4 mb-6">
          <form onSubmit={handleSearch} className={isStudio ? 'flex flex-col gap-3' : 'grid grid-cols-1 lg:grid-cols-12 gap-3'}>
            <div className={isStudio ? 'flex flex-col lg:flex-row gap-3' : 'contents'}>
            <div className={`relative ${isStudio ? 'flex-1' : 'lg:col-span-2'}`}>
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search prompt, response, website, user..."
                value={searchInput}
                onChange={(event) => setSearchInput(event.target.value)}
                className="input-field w-full pl-9"
              />
            </div>
            <select
              value={surface}
              onChange={(event) => setSurface(event.target.value)}
              className={`input-field !pr-10 ${isStudio ? 'lg:w-56' : 'lg:col-span-2'}`}
            >
              <option value="">All surfaces</option>
              {Object.entries(surfaceLabels).map(([key, label]) => (
                <option key={key} value={key}>{label}</option>
              ))}
            </select>
            {isStudio && (
              <select
                value={studioResult}
                onChange={(event) => { setStudioResult(event.target.value); setCurrentPage(1); }}
                className="input-field !pr-10 lg:w-48"
              >
                <option value="">All results</option>
                <option value="shipped">Shipped</option>
                <option value="waiting">Waiting</option>
                <option value="answered">Answered</option>
                <option value="config">Config saved</option>
                <option value="no_change">No change</option>
                <option value="stopped">Stopped</option>
              </select>
            )}
            {isStudio && (
              <button type="submit" className="btn-primary lg:w-28">Search</button>
            )}
            </div>
            {isStudio && (
              <div className="flex flex-col sm:flex-row gap-3">
                <select
                  value={turnMode}
                  onChange={(event) => { setTurnMode(event.target.value); setCurrentPage(1); }}
                  className="input-field !pr-10 sm:w-48"
                >
                  <option value="">All turn modes</option>
                  <option value="tweak">Tweak</option>
                  <option value="new_feature">New feature</option>
                  <option value="redesign">Redesign</option>
                </select>
                <select
                  value={hasFailures}
                  onChange={(event) => { setHasFailures(event.target.value); setCurrentPage(1); }}
                  className="input-field !pr-10 sm:w-48"
                >
                  <option value="">Tool retries</option>
                  <option value="true">Had tool retries</option>
                  <option value="false">No tool retries</option>
                </select>
                <input
                  type="number"
                  min="0"
                  placeholder="Min rounds"
                  value={minIterations}
                  onChange={(event) => { setMinIterations(event.target.value); setCurrentPage(1); }}
                  className="input-field sm:w-36"
                />
              </div>
            )}
            {!isStudio && (
              <>
                <select
                  value={status}
                  onChange={(event) => { setStatus(event.target.value); setCurrentPage(1); }}
                  className="input-field !pr-10 lg:col-span-2"
                >
                  <option value="">All statuses</option>
                  <option value="success">Success</option>
                  <option value="partial">Partial</option>
                  <option value="failed">Failed</option>
                  <option value="error">Error</option>
                  <option value="no_change">No change</option>
                  <option value="pending_user_choice">User choice</option>
                </select>
                <select
                  value={outcomeLabel}
                  onChange={(event) => { setOutcomeLabel(event.target.value); setCurrentPage(1); }}
                  className="input-field !pr-10 lg:col-span-2"
                >
                  <option value="">All outcomes</option>
                  {Object.entries(outcomeLabels).map(([key, label]) => (
                    <option key={key} value={key}>{label}</option>
                  ))}
                </select>
                <select
                  value={hasFailures}
                  onChange={(event) => { setHasFailures(event.target.value); setCurrentPage(1); }}
                  className="input-field !pr-10 lg:col-span-2"
                >
                  <option value="">Failures any</option>
                  <option value="true">Has failures</option>
                  <option value="false">No failures</option>
                </select>
                <input
                  type="number"
                  min="0"
                  placeholder="Min rounds"
                  value={minIterations}
                  onChange={(event) => { setMinIterations(event.target.value); setCurrentPage(1); }}
                  className="input-field lg:col-span-1"
                />
                <input
                  type="number"
                  min="0"
                  placeholder="Min visions"
                  value={minVisionChecks}
                  onChange={(event) => { setMinVisionChecks(event.target.value); setCurrentPage(1); }}
                  className="input-field lg:col-span-1"
                />
                <button type="submit" className="btn-primary lg:col-span-2">
                  Search
                </button>
              </>
            )}
          </form>
        </div>

        {isStudio ? (
          <StudioAuditBoard
            audits={audits}
            stats={stats}
            loading={loading}
            activeResult={studioResult}
            onSelectResult={(value) => { setStudioResult(value); setCurrentPage(1); }}
            onOpen={(row) => navigate(`/ai-customizations/${row.id}`)}
            sortBy={sortBy}
            sortOrder={sortOrder}
            onSort={handleSort}
            footer={totalPages > 1 && (
              <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} />
            )}
          />
        ) : (
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <Table
              columns={columns}
              data={audits}
              loading={loading}
              emptyMessage="No AI audit records found"
              sortBy={sortBy}
              sortOrder={sortOrder}
              onSort={handleSort}
              onRowClick={(row) => navigate(`/ai-customizations/${row.id}`)}
            />
            {totalPages > 1 && (
              <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} />
            )}
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AICustomizationAudits;
